import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { SiteNavigation } from "../components/navigation/SiteNavigation";
import { toast } from "sonner";
import {
  Sparkles, Play, Music, Video, Heart, MessageCircle, Send, User,
  Flame, Image, Repeat2, Bookmark, Trash2, Pencil, X, Globe, ArrowRight, Smile,
  Copy, Check
} from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import ReportButton from "../components/ReportButton";
import BlockUserButton from "../components/BlockUserButton";
import PlatformEmbed from "../components/PlatformEmbed";
import { useImageEditor } from "../components/image-editor/ImageEditorProvider";

const Headlines = () => {
  const editImage = useImageEditor();
  const { isAuthenticated, token, user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [posting, setPosting] = useState(false);
  const [shareToCommunity, setShareToCommunity] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState("trending");
  const [expandedPost, setExpandedPost] = useState(null);
  const [replies, setReplies] = useState({});
  const [replyText, setReplyText] = useState("");
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [emojiTarget, setEmojiTarget] = useState(null); // null | 'compose' | 'edit-<id>' | 'reply-<id>'
  const [copiedPostId, setCopiedPostId] = useState(null);
  const fileInputRef = useRef(null);
  // Support fix (ticket 274782): caret position for emoji insertion.
  const composeTextRef = useRef(null);
  const editTextRef = useRef(null);

  const copyPostText = async (postId, text) => {
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for insecure contexts
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedPostId(postId);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedPostId((cur) => (cur === postId ? null : cur)), 2000);
    } catch {
      toast.error("Copy failed — long-press or select the text manually");
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => {
    if (activeSection === "mine" && isAuthenticated) fetchMine();
  }, [activeSection, isAuthenticated]);

  const fetchAll = async () => {
    try {
      const [postsRes, feedRes] = await Promise.all([
        axios.get(`${API}/community/posts`),
        axios.get(`${API}/feed/today`)
      ]);
      setPosts(postsRes.data);
      setFeed(feedRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchMine = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/community/posts/mine`, { headers: { Authorization: `Bearer ${token}` } });
      setMyPosts(res.data);
    } catch (e) { console.error(e); }
  };

  const handleMediaUpload = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    // Accept iPhone HEIC/HEIF alongside standard images/videos
    const okType = file.type.startsWith("image/") || file.type.startsWith("video/") ||
                   /\.(heic|heif|jpg|jpeg|png|gif|webp|mp4|mov|webm)$/i.test(file.name);
    if (!okType) { toast.error("Only images and videos are allowed"); return; }
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`File too large — ${(file.size / 1024 / 1024).toFixed(1)}MB. The limit is 50MB. Try compressing it first.`);
      e.target.value = "";
      return;
    }
    file = await editImage(file, { maxBytes: 50 * 1024 * 1024 });
    if (!file) { e.target.value = ""; return; }
    setUploading(true);
    const toastId = toast.loading(`Uploading ${(file.size / 1024 / 1024).toFixed(1)}MB…`);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/community/upload-media`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setMediaPreview({ url: res.data.url, type: res.data.type });
      toast.success("Uploaded — ready to post", { id: toastId });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Upload failed";
      toast.error(detail, { id: toastId });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handlePost = async () => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    // Allow posting if text, media, or a link is detected
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = newPost.match(urlRegex);
    if (!newPost.trim() && !mediaPreview && (!urls || urls.length === 0)) return;
    setPosting(true);
    try {
      const body = {
        text: newPost.trim(),
        visibility: shareToCommunity ? "community" : "personal"
      };
      if (mediaPreview?.type === "image") body.image_url = mediaPreview.url;
      if (mediaPreview?.type === "video") body.video_url = mediaPreview.url;
      if (urls && urls.length > 0) body.link_url = urls[0];
      await axios.post(`${API}/community/posts`, body, { headers: { Authorization: `Bearer ${token}` } });
      setNewPost(""); setMediaPreview(null);
      const wasShared = shareToCommunity;
      setShareToCommunity(false);
      fetchAll();
      if (activeSection === "mine") fetchMine();
      toast.success(wasShared ? "Posted to Community!" : "Posted to your feed!");
    } catch (e) { toast.error("Failed to post"); } finally { setPosting(false); }
  };

  const handleLike = async (postId) => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    try { await axios.post(`${API}/community/posts/${postId}/like`, {}, { headers: { Authorization: `Bearer ${token}` } }); fetchAll(); } catch { /* noop */ }
  };

  const handleRepost = async (post) => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    try { await axios.post(`${API}/community/posts`, { text: "", repost_id: post.post_id, visibility: "community" }, { headers: { Authorization: `Bearer ${token}` } }); fetchAll(); if (activeSection === "mine") fetchMine(); toast.success("Reposted!"); } catch { /* noop */ }
  };

  const handleBookmark = async (postId) => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    try { const res = await axios.post(`${API}/community/posts/${postId}/bookmark`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success(res.data.bookmarked ? "Saved!" : "Removed"); } catch { /* noop */ }
  };

  const handleDelete = async (postId) => {
    try { await axios.delete(`${API}/community/posts/${postId}`, { headers: { Authorization: `Bearer ${token}` } }); fetchAll(); } catch { /* noop */ }
  };

  const startEdit = (post) => {
    setEditingPostId(post.post_id);
    setEditingText(post.text || "");
  };

  const cancelEdit = () => {
    setEditingPostId(null);
    setEditingText("");
  };

  const saveEdit = async (postId) => {
    if (!editingText.trim()) { toast.error("Post cannot be empty"); return; }
    try {
      await axios.patch(`${API}/community/posts/${postId}`, { text: editingText.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Post updated");
      cancelEdit();
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const insertEmoji = (emojiObj) => {
    const emoji = emojiObj?.emoji || "";
    if (!emoji) return;
    // Support fix (ticket 274782): insert at the cursor instead of appending to the end.
    const atCaret = (ref, setText) => {
      const el = ref.current;
      setText((prev) => {
        const start = el && typeof el.selectionStart === "number" ? el.selectionStart : prev.length;
        const end = el && typeof el.selectionEnd === "number" ? el.selectionEnd : start;
        const pos = start + emoji.length;
        if (el) setTimeout(() => { try { el.focus(); el.setSelectionRange(pos, pos); } catch (_) {} }, 0);
        return prev.slice(0, start) + emoji + prev.slice(end);
      });
    };
    if (emojiTarget === "compose") {
      atCaret(composeTextRef, setNewPost);
    } else if (emojiTarget?.startsWith("edit-")) {
      atCaret(editTextRef, setEditingText);
    }
    setEmojiTarget(null);
  };

  const toggleReplies = async (postId) => {
    if (expandedPost === postId) { setExpandedPost(null); return; }
    setExpandedPost(postId);
    try { const res = await axios.get(`${API}/community/posts/${postId}/replies`); setReplies(prev => ({...prev, [postId]: res.data})); } catch { /* noop */ }
  };

  const handleReply = async (postId) => {
    if (!replyText.trim()) return;
    try { await axios.post(`${API}/community/posts/${postId}/replies`, { text: replyText.trim() }, { headers: { Authorization: `Bearer ${token}` } }); setReplyText(""); toggleReplies(postId); fetchAll(); } catch (e) { toast.error("Failed"); }
  };

  const timeAgo = (d) => { const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000); if (m < 1) return "now"; if (m < 60) return `${m}m`; const h = Math.floor(m/60); if (h < 24) return `${h}h`; return `${Math.floor(h/24)}d`; };

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="headlines-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong public-navigation-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-14 h-14 object-contain" style={{mixBlendMode:"lighten"}} />
          </Link>
          <SiteNavigation scope="headlines" />
          {isAuthenticated ? (
            <Button onClick={() => navigate("/fan-dashboard")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full px-6" data-testid="dash-btn">My Dashboard</Button>
          ) : (
            <Button onClick={() => navigate("/auth")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full px-6" data-testid="join-btn">Join Free</Button>
          )}
        </div>
      </header>

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D14A85]/10 border border-[#D14A85]/30 mb-4">
              <Globe className="w-4 h-4 text-[#D14A85]" />
              <span className="text-sm text-[#D14A85] font-medium">Live Now</span>
            </div>
            <h1 className="font-['Unbounded'] text-3xl sm:text-4xl font-bold text-white mb-2">
              Global Entertainment <span className="text-[#EC008C]">Headlines</span>
            </h1>
            <p className="text-[#8A92A0]">The pulse of entertainment. Post, discover, interact.</p>
          </div>

          {/* Join CTA (non-auth only) */}
          {!isAuthenticated && (
            <div className="card p-6 mb-6 text-center border-[#EC008C]/20 bg-gradient-to-r from-[#EC008C]/5 to-[#D14A85]/5" data-testid="join-cta">
              <h3 className="font-['Unbounded'] text-lg font-bold text-white mb-2">Create your account now to join the global party of StardomFans.com</h3>
              <p className="text-[#8A92A0] text-sm mb-4">Post, like, repost, upload videos & interact with artists worldwide.</p>
              <Button onClick={() => navigate("/auth")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full px-8 font-semibold" data-testid="join-party-btn">
                Join the Party <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Section Tabs */}
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            {[
              { key: "trending", label: "Trending", auth: false },
              { key: "latest", label: "Latest", auth: false },
              { key: "mine", label: "My Feed", auth: true },
              { key: "drops", label: "New Drops", auth: false }
            ].filter(t => !t.auth || isAuthenticated).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveSection(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeSection === tab.key ? "bg-[#EC008C] text-[#000000]" : "bg-[#0A0E18] text-[#8A92A0] hover:text-white border border-[#1A2030]"
                }`}
                data-testid={`tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Post Composer (auth only) */}
          {isAuthenticated && (
            <div className="card p-5 mb-6" data-testid="headline-composer">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1A2030] flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-[#8A92A0]" />}
                </div>
                <div className="flex-1">
                  <Textarea ref={composeTextRef} value={newPost} onChange={(e) => setNewPost(e.target.value)} placeholder="Share entertainment news, thoughts, media..." className="bg-[#050810] border-[#1A2030] text-white resize-none min-h-[160px] text-base leading-relaxed p-4" data-testid="headline-textarea" />
                  {mediaPreview && (
                    <div className="relative mt-3 rounded-xl overflow-hidden">
                      {mediaPreview.type === "image" ? <img src={mediaPreview.url} alt="Edited post preview" className="block mx-auto w-auto h-auto max-w-full max-h-[480px] rounded-xl" /> : <video src={mediaPreview.url} className="w-full max-h-48 rounded-xl" controls />}
                      <button onClick={() => setMediaPreview(null)} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center"><X className="w-3 h-3 text-white" /></button>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2 relative">
                      <input data-testid="headline-media-input" ref={fileInputRef} type="file" accept="image/*,video/*,.heic,.heif" onChange={handleMediaUpload} className="hidden" />
                      <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="p-2 rounded-lg text-[#8A92A0] hover:text-[#EC008C] hover:bg-[#EC008C]/10" data-testid="upload-media-btn"><Image className="w-5 h-5" /></button>
                      <button
                        onClick={() => setEmojiTarget(emojiTarget === "compose" ? null : "compose")}
                        className="p-2 rounded-lg text-[#8A92A0] hover:text-[#EC008C] hover:bg-[#EC008C]/10"
                        data-testid="emoji-picker-btn"
                        aria-label="Insert emoji"
                      ><Smile className="w-5 h-5" /></button>
                      {emojiTarget === "compose" && (
                        <div className="absolute top-full left-0 mt-2 z-50 shadow-xl rounded-xl overflow-hidden" data-testid="emoji-picker-panel">
                          <EmojiPicker
                            onEmojiClick={insertEmoji}
                            theme="dark"
                            width={330}
                            height={380}
                            searchDisabled={false}
                            skinTonesDisabled={false}
                            previewConfig={{ showPreview: false }}
                            lazyLoadEmojis
                          />
                        </div>
                      )}
                      {uploading && <span className="text-xs text-[#EC008C]">Uploading...</span>}
                    </div>
                    <Button onClick={handlePost} disabled={posting || (!newPost.trim() && !mediaPreview)} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full px-6" data-testid="post-headline-btn">
                      {posting ? "..." : <><Send className="w-4 h-4 mr-1" /> Post</>}
                    </Button>
                  </div>
                  {/* Visibility toggle — personal by default, opt-in to community */}
                  <label className="flex items-center gap-2 mt-3 pt-3 border-t border-[#1A2030] cursor-pointer select-none" data-testid="share-community-toggle-label">
                    <input
                      type="checkbox"
                      checked={shareToCommunity}
                      onChange={(e) => setShareToCommunity(e.target.checked)}
                      className="w-4 h-4 rounded border-[#1A2030] bg-[#050810] accent-[#EC008C] cursor-pointer"
                      data-testid="share-community-toggle"
                    />
                    <Globe className="w-3.5 h-3.5 text-[#8A92A0]" />
                    <span className="text-xs text-[#8A92A0]">
                      Also share to Community feed
                      {!shareToCommunity && <span className="text-[#4A5260]"> · your post stays on your own feed</span>}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner" /></div>
          ) : (
            <div className="space-y-4">
              {/* New Drops Section */}
              {activeSection === "drops" && feed.length > 0 && (
                <div className="space-y-3 mb-6">
                  {feed.map((item) => (
                    <div key={item.content_id} className="card p-4 flex items-center gap-4" data-testid={`drop-${item.content_id}`}>
                      <div className="w-14 h-14 rounded-xl bg-[#0A0E18] flex items-center justify-center flex-shrink-0">
                        {item.content_type === "music" ? <Music className="w-6 h-6 text-[#EC008C]/50" /> : <Video className="w-6 h-6 text-[#FF6BC5]/50" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${item.content_type === "music" ? "bg-[#EC008C]/10 text-[#EC008C]" : "bg-[#FF6BC5]/10 text-[#FF6BC5]"}`}>{item.content_type}</span>
                        <h3 className="font-semibold text-white truncate mt-1">{item.title}</h3>
                        {item.artist && <p className="text-xs text-[#8A92A0]">{item.artist.stage_name}</p>}
                      </div>
                      {item.artist && (
                        <Link to={`/artist/${item.artist.artist_id}`}>
                          <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full px-4 text-sm"><Play className="w-3 h-3 mr-1" /> Listen</Button>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {activeSection === "drops" && feed.length === 0 && (
                <div className="card p-12 text-center"><Music className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" /><p className="text-[#8A92A0]">No new drops today — check back soon!</p></div>
              )}

              {/* Posts Feed */}
              {(activeSection === "trending" || activeSection === "latest" || activeSection === "mine") && (
                (() => {
                  const currentPosts = activeSection === "mine" ? myPosts : posts;
                  if (currentPosts.length === 0) {
                    return (
                      <div className="card p-12 text-center" data-testid={`empty-${activeSection}`}>
                        <MessageCircle className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
                        <h3 className="text-white font-semibold mb-2">
                          {activeSection === "mine" ? "Your feed is empty" : "No headlines yet"}
                        </h3>
                        <p className="text-[#8A92A0] text-sm">
                          {activeSection === "mine"
                            ? "Share your first post above — it'll live here on your personal feed."
                            : (isAuthenticated ? "Be the first to post!" : "Join to start the conversation!")}
                        </p>
                      </div>
                    );
                  }
                  return currentPosts.map((post) => (
                    <div key={post.post_id} className="card p-5" data-testid={`headline-${post.post_id}`}>
                      {post.repost_id && (
                        <div className="flex items-center gap-2 text-xs text-[#8A92A0] mb-3 pb-2 border-b border-[#1A2030]"><Repeat2 className="w-3.5 h-3.5" /><span>{post.user_name} reposted</span></div>
                      )}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1A2030] flex-shrink-0 overflow-hidden flex items-center justify-center">
                          {post.user_picture ? <img src={post.user_picture} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-[#8A92A0]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white text-sm">{post.user_name}</span>
                            <span className="text-xs text-[#4A5260]">{timeAgo(post.created_at)}</span>
                          </div>
                          {editingPostId === post.post_id ? (
                            <div className="mt-2 space-y-2" data-testid={`edit-form-${post.post_id}`}>
                              <Textarea
                                ref={editTextRef}
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                rows={3}
                                className="bg-[#050810] border-[#1A2030] text-white text-sm"
                                data-testid={`edit-textarea-${post.post_id}`}
                              />
                              <div className="flex gap-2 items-center relative">
                                <Button
                                  onClick={() => saveEdit(post.post_id)}
                                  className="bg-[#EC008C] text-black hover:bg-[#FF3399] h-8 text-xs px-3"
                                  data-testid={`edit-save-${post.post_id}`}
                                >Save</Button>
                                <Button
                                  variant="outline"
                                  onClick={cancelEdit}
                                  className="h-8 text-xs px-3 border-[#1A2030]"
                                  data-testid={`edit-cancel-${post.post_id}`}
                                >Cancel</Button>
                                <button
                                  onClick={() => setEmojiTarget(emojiTarget === `edit-${post.post_id}` ? null : `edit-${post.post_id}`)}
                                  className="p-1.5 rounded-lg text-[#8A92A0] hover:text-[#EC008C] hover:bg-[#EC008C]/10"
                                  data-testid={`edit-emoji-btn-${post.post_id}`}
                                  aria-label="Insert emoji"
                                ><Smile className="w-4 h-4" /></button>
                                {emojiTarget === `edit-${post.post_id}` && (
                                  <div className="absolute top-full left-0 mt-2 z-50 shadow-xl rounded-xl overflow-hidden">
                                    <EmojiPicker
                                      onEmojiClick={insertEmoji}
                                      theme="dark"
                                      width={330}
                                      height={380}
                                      searchDisabled={false}
                                      skinTonesDisabled={false}
                                      previewConfig={{ showPreview: false }}
                                      lazyLoadEmojis
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            post.text && <p className="text-[#8A92A0] mt-2 text-sm whitespace-pre-wrap break-words select-text cursor-text" data-testid={`post-text-${post.post_id}`}>{post.text}</p>
                          )}
                          {post.repost_data && (
                            <div className="mt-3 p-3 rounded-xl border border-[#1A2030] bg-[#050810]">
                              <p className="text-xs text-[#4A5260] mb-1">@{post.repost_data.user_name}</p>
                              {post.repost_data.text && (
                                <p className="text-sm text-[#8A92A0] whitespace-pre-wrap break-words">{post.repost_data.text}</p>
                              )}
                              {post.repost_data.image_url && <img src={post.repost_data.image_url} alt="" className="mt-2 block mx-auto w-auto h-auto max-w-full max-h-[480px] rounded-lg" />}
                              {post.repost_data.video_url && (
                                <video src={post.repost_data.video_url} className="mt-2 rounded-lg w-full max-h-72" controls preload="metadata" />
                              )}
                              {post.repost_data.link_url && <PlatformEmbed url={post.repost_data.link_url} meta={post.repost_data.link_meta} />}
                            </div>
                          )}
                          {post.image_url && <div className="mt-3 rounded-xl overflow-hidden"><img src={post.image_url} alt="" className="block mx-auto w-auto h-auto max-w-full max-h-[640px] rounded-xl" /></div>}
                          {post.video_url && <div className="mt-3 rounded-xl overflow-hidden"><video src={post.video_url} className="w-full max-h-72 rounded-xl" controls preload="metadata" /></div>}
                          {post.link_url && (
                            <PlatformEmbed url={post.link_url} meta={post.link_meta} />
                          )}

                          {/* Actions */}
                          <div className="flex items-center gap-5 mt-3">
                            <button onClick={() => handleLike(post.post_id)} className="flex items-center gap-1 text-[#8A92A0] hover:text-[#D14A85] transition-colors text-sm" data-testid={`like-${post.post_id}`}><Heart className="w-4 h-4" /> {post.likes || 0}</button>
                            <button onClick={() => toggleReplies(post.post_id)} className="flex items-center gap-1 text-[#8A92A0] hover:text-[#FF6BC5] transition-colors text-sm" data-testid={`comment-${post.post_id}`}><MessageCircle className="w-4 h-4" /> {post.comment_count || 0}</button>
                            <button onClick={() => handleRepost(post)} className="flex items-center gap-1 text-[#8A92A0] hover:text-[#8DB87A] transition-colors text-sm" data-testid={`repost-${post.post_id}`}><Repeat2 className="w-4 h-4" /> {post.repost_count || 0}</button>
                            {post.text && (
                              <button
                                onClick={() => copyPostText(post.post_id, post.text)}
                                className={`flex items-center gap-1 transition-colors text-sm ${
                                  copiedPostId === post.post_id
                                    ? "text-[#EC008C]"
                                    : "text-[#8A92A0] hover:text-[#EC008C]"
                                }`}
                                data-testid={`copy-${post.post_id}`}
                                title="Copy post text"
                              >
                                {copiedPostId === post.post_id ? (
                                  <>
                                    <Check className="w-4 h-4" />
                                    <span className="text-xs">Copied</span>
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-4 h-4" />
                                    <span className="text-xs sr-only">Copy</span>
                                  </>
                                )}
                              </button>
                            )}
                            <button onClick={() => handleBookmark(post.post_id)} className="text-[#8A92A0] hover:text-[#EC008C] transition-colors text-sm ml-auto" data-testid={`save-${post.post_id}`}><Bookmark className="w-4 h-4" /></button>
                            {user && post.user_id === user.user_id && (
                              <>
                                <button
                                  onClick={() => startEdit(post)}
                                  className="text-[#8A92A0] hover:text-[#EC008C] transition-colors"
                                  data-testid={`edit-${post.post_id}`}
                                  title="Edit post"
                                ><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => handleDelete(post.post_id)} className="text-[#8A92A0] hover:text-[#D14A85]" data-testid={`delete-${post.post_id}`} title="Delete post"><Trash2 className="w-4 h-4" /></button>
                              </>
                            )}
                            {user && post.user_id !== user.user_id && (
                              <>
                                <ReportButton contentType="post" contentId={post.post_id} token={token} iconOnly />
                                <BlockUserButton targetUserId={post.user_id} targetName={post.user_name} token={token} onBlocked={fetchAll} iconOnly />
                              </>
                            )}
                          </div>

                          {/* Replies */}
                          {expandedPost === post.post_id && (
                            <div className="mt-4 pt-4 border-t border-[#1A2030] space-y-3">
                              {(replies[post.post_id] || []).map((r) => (
                                <div key={r.reply_id} className="flex items-start gap-2 pl-2">
                                  <div className="w-7 h-7 rounded-full bg-[#1A2030] flex-shrink-0 flex items-center justify-center overflow-hidden">
                                    {r.user_picture ? <img src={r.user_picture} alt="" className="w-full h-full object-cover" /> : <User className="w-3.5 h-3.5 text-[#8A92A0]" />}
                                  </div>
                                  <div><span className="text-xs font-medium text-white">{r.user_name}</span><span className="text-xs text-[#4A5260] ml-2">{timeAgo(r.created_at)}</span><p className="text-xs text-[#8A92A0] mt-0.5">{r.text}</p></div>
                                </div>
                              ))}
                              {isAuthenticated && (
                                <div className="flex items-center gap-2 pl-2">
                                  <input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Reply..." className="flex-1 bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-xs text-white placeholder-[#52525B]" data-testid={`reply-input-${post.post_id}`} />
                                  <button onClick={() => handleReply(post.post_id)} disabled={!replyText.trim()} className="text-[#EC008C] disabled:opacity-50"><Send className="w-4 h-4" /></button>
                                </div>
                              )}
                              {!isAuthenticated && <p className="text-xs text-[#4A5260] pl-2"><Link to="/auth" className="text-[#EC008C] hover:underline">Sign in</Link> to reply</p>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ));
                })()
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Headlines;
